import { AnimatePresence, Breadcrumbs, EntityCardSize, ModuleBase } from '@/components';
import { LinkModel, ModuleBaseModel, ModuleBasePropsModel, ReportSearchModel } from '@/types';
import Flicking, { ChangedEvent, WillChangeEvent } from '@egjs/react-flicking';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AutoPlay } from '@egjs/flicking-plugins';
import tailwindConfig from '@/tailwind.screens';
import ReportCard from '@/components/report/ReportCard';
import { motion } from 'framer-motion';
import ArrowRightLight from '@/assets/arrowRightLight.svg';

type HeroCarouselModel = ModuleBaseModel<
  {
    title?: string;
    featuredReports?: ReportSearchModel[];
    breadcrumb?: LinkModel[];
  } & ModuleBasePropsModel
>;

const HeroCarousel = (data: HeroCarouselModel) => {
  const {
    props: { title, featuredReports, breadcrumb },
  } = data;

  const [alignment, setAlignment] = useState('prev');
  const [panelsPerView, setPanelsPerView] = useState(3);
  const [currentSlide, setCurrentSlide] = useState(0);

  const carousel = useRef<Flicking>(null);

  const plugins = [
    new AutoPlay({
      duration: 6000,
      direction: 'NEXT',
      stopOnHover: true,
    }),
  ];

  const itemsList = useMemo(() => {
    if (featuredReports && featuredReports.length > 0 && featuredReports.length < 4) {
      let newList = [...featuredReports];
      while (newList.length < 4) {
        newList = [...newList, ...featuredReports];
      }
      return newList;
    }
    return featuredReports;
  }, [featuredReports]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
        setAlignment('prev');
        setPanelsPerView(3);
      } else {
        setAlignment('center');
        setPanelsPerView(2);
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onCarouselChanged = (event: ChangedEvent<Flicking>) => {
    setCurrentSlide(event.index);
  };

  const onCarouselWillChange = (event: WillChangeEvent<Flicking>) => {
    setCurrentSlide(event.index);
  };

  const onCarouselMove = () => {
    const flicking = carousel.current;
    if (flicking) {
      const panels = flicking.visiblePanels;
      panels.forEach((panel) => {
        const progress = panel.outsetProgress;
        const el = panel.element;
        const target = el;
        target.style.transform = `scale(${1 - Math.abs(progress * 0.65)})`;

        if (window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
          target.style.zIndex = 10 - Math.floor(progress * 10) + '';
        } else {
          target.style.zIndex = (progress > -0.6 && progress < 0.6 ? 10 : 5) + '';
        }

        if (progress < -0.3 && window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
          target.style.opacity = '0';
        } else {
          target.style.opacity = '1';
        }
      });
    }
  };

  const onCarouselReady = () => {
    const flicking = carousel.current;
    if (flicking) {
      const panels = flicking.panels;
      panels.forEach((panel, index) => {
        const progress = panel.outsetProgress;
        const el = panel.element;
        const target = el;
        if (window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
          target.style.zIndex = 10 - Math.floor(progress * 10) + '';
          target.style.transform = `scale(${1 - Math.abs(progress * 0.65)})`;
        } else {
          target.style.zIndex = index === 0 ? '10' : '5';
          target.style.transform = `scale(${index === 0 ? 1 : 0.566667})`;
        }

        if (progress < -0.3 && window.innerWidth >= parseInt(tailwindConfig.screens.md, 10)) {
          target.style.opacity = '0';
        } else {
          target.style.opacity = '1';
        }
      });
    }
  };

  const reportCardClick = (e: React.MouseEvent, clickedIndex: number) => {
    if (currentSlide !== clickedIndex) {
      carousel.current?.moveTo(clickedIndex).catch(() => void 0);
      e.preventDefault();
    }
  };

  let realIndex = currentSlide;

  if (featuredReports && featuredReports.length > 0) {
    while (realIndex >= featuredReports.length) {
      realIndex -= featuredReports.length;
    }
  }

  const _moveActiveCard = (direction: number) => {
    if (!carousel.current?.animating) {
      if (direction >= 0) {
        carousel.current?.next();
      } else {
        carousel.current?.prev();
      }
    }
  };

  if (!featuredReports || featuredReports.length === 0) {
    return null;
  }

  return (
    <>
      <ModuleBase data={data} noPadding>
        <div className="relative pt-6 pb-12 md:py-16 print:hidden">
          <div className="container md:hidden">
            <div className="flex flex-col justify-center pt-3 pb-6">
              <h2 className="color-from-bg text-center text-[26px] leading-8 font-light">{title}</h2>
            </div>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="hidden min-h-[400px] w-5/12 md:block" />
            <div className="w-full md:w-7/12">
              <Flicking
                ref={carousel}
                plugins={plugins}
                preventClickOnDrag
                preventDefaultOnDrag
                circular
                interruptable
                align={alignment}
                panelsPerView={panelsPerView}
                hideBeforeInit={true}
                onChanged={onCarouselChanged}
                onWillChange={onCarouselWillChange}
                onReady={onCarouselReady}
                onMove={onCarouselMove}
              >
                {itemsList &&
                  itemsList.map((report, index) => (
                    <div
                      key={index}
                      className="panel relative z-0 inline-flex justify-center transition-opacity duration-500 md:justify-start"
                    >
                      <ReportCard
                        className="shrink-0 transition-all duration-500 [&_.report-card]:!bg-transparent [&_.title]:hidden"
                        data={report}
                        size={EntityCardSize.LARGE}
                        hideTitle
                        onClick={(e) => reportCardClick(e, index)}
                      />
                    </div>
                  ))}
              </Flicking>
            </div>
          </div>
          <div className="color-from-bg container flex flex-col-reverse pt-0 md:pointer-events-none md:absolute md:inset-x-0 md:top-0 md:z-[4] md:m-auto md:h-full md:flex-col md:py-16">
            <div className="hidden w-full pt-0 md:block md:w-5/12 md:pr-12 lg:pr-16">
              <h2 className="text-[26px] leading-8 font-light">{title}</h2>
            </div>
            {itemsList && itemsList[realIndex].title && (
              <div className="flex flex-shrink-0 flex-grow-1 flex-col justify-center pt-6 md:pt-0 lg:pt-6">
                <AnimatePresence>
                  <div className="flex">
                    <motion.div
                      initial={{ x: -100, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -100, opacity: 0 }}
                      transition={{
                        type: 'tween',
                        ease: 'easeOut',
                        duration: 0.5,
                      }}
                      key={itemsList[realIndex].reportId}
                      className="flex flex-col gap-6 md:w-5/12 md:pr-12 lg:pr-16"
                    >
                      <h1 className="text-3xl leading-10 font-light md:line-clamp-2 md:text-[40px] md:leading-[35px] lg:line-clamp-3 lg:text-[45px] lg:leading-[55px]">
                        {itemsList[realIndex].title}
                      </h1>

                      <div
                        className="body-1 md:line-clamp-6 xl:line-clamp-none md:pointer-events-auto"
                        dangerouslySetInnerHTML={{ __html: itemsList[realIndex].shortenedDescription }}
                      />
                    </motion.div>
                  </div>
                </AnimatePresence>
              </div>
            )}
            <div className="pointer-events-auto flex items-center justify-center gap-1 py-3 md:justify-start md:py-0 lg:max-w-max">
              <button className="cursor-pointer px-2" onClick={() => _moveActiveCard(-1)}>
                <ArrowRightLight className="fill-from-bg rotate-180" />
              </button>
              <div>
                {realIndex + 1}
                <span className="px-1">/</span>
                {featuredReports.length}
              </div>
              <button className="cursor-pointer px-2" onClick={() => _moveActiveCard(1)}>
                <ArrowRightLight className="fill-from-bg" />
              </button>
            </div>
          </div>
        </div>
      </ModuleBase>
      {breadcrumb && (
        <div className="container">
          <Breadcrumbs className="py-3" data={breadcrumb} />
        </div>
      )}
    </>
  );
};

export default HeroCarousel;
